import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { APP_NAME } from '@imsenta/shared';
import { LogoTile } from '@/components/Logo';
import { Alert, Button, Card, Field, Input, Spinner } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { useSession } from '@/lib/session';

interface InvitePreview {
  email: string;
  role: string;
  organisation: string;
  requiresPassword: boolean;
}

export function AcceptInvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [form, setForm] = useState({ name: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get<InvitePreview>(`/auth/invitations/${token}`),
    retry: false,
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/invitations/accept', {
        token,
        name: form.name || undefined,
        password: form.password || undefined,
      });
      await refresh();
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not accept the invitation.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-full place-items-center p-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <LogoTile size={32} />
          <span className="text-base font-semibold tracking-[-0.02em] text-ink-900">{APP_NAME}</span>
        </div>

        <Card className="p-6">
          {isLoading ? (
            <Spinner label="Checking your invitation…" />
          ) : loadError ? (
            <>
              <Alert tone="danger" title="This invitation cannot be used">
                {loadError instanceof ApiError ? loadError.message : 'The link may have expired or already been used.'}
              </Alert>
              <Button className="mt-4 w-full" onClick={() => navigate('/signin')}>
                Go to sign in
              </Button>
            </>
          ) : data ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h1 className="text-lg font-semibold text-ink-900">Join {data.organisation}</h1>
                <p className="mt-1 text-[13.5px] text-ink-500">
                  You have been invited as <span className="font-medium capitalize">{data.role}</span> using {data.email}.
                </p>
              </div>

              {error ? <Alert tone="danger">{error}</Alert> : null}

              {data.requiresPassword ? (
                <>
                  <Field label="Your name" required>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
                  </Field>
                  <Field label="Choose a password" required hint="At least 12 characters, with upper case, lower case and a number.">
                    <Input
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      required
                      autoComplete="new-password"
                    />
                  </Field>
                </>
              ) : (
                <Alert tone="info">You already have an account, so accepting will simply add this organisation to it.</Alert>
              )}

              <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
                Accept invitation
              </Button>
            </form>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
